<template>
  <div class="search-container">
    <form class="mb-8" @submit.prevent="performSearch">
      <div class="flex gap-4 max-w-2xl">
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="placeholder"
          class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-surface-dark"
          minlength="2"
          required
        />
        <button
          type="submit"
          class="px-6 py-2 bg-surface-dark text-white rounded-lg hover:bg-accent-2 transition-colors"
          :disabled="loading"
        >
          {{ loading ? t.loading : t.button }}
        </button>
      </div>
    </form>

    <div v-if="hasSearched && !loading" class="search-results" role="region" aria-live="polite" :aria-busy="loading">
      <div v-if="results.length > 0">
        <div
          v-for="result in paginatedResults"
          :key="result.identifier"
          class="search-result-item mb-6 p-4 border-b border-gray-200"
        >
          <h2 class="text-xl font-semibold mb-2">
            <a :href="result.uri" class="text-surface-dark hover:underline">
              {{ result.title }}
            </a>
          </h2>
          <p class="text-gray-600 text-sm mb-2">{{ result.uri }}</p>
          <p v-if="result.teaser" class="text-gray-700">
            {{ truncate(result.teaser, 150) }}
          </p>
        </div>

        <nav v-if="totalPages > 1" class="pagination mt-8 flex justify-center gap-2" :aria-label="t.pagination">
          <button
            :disabled="currentPage === 1"
            :aria-label="t.firstPage"
            class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
            @click="currentPage = 1"
          >
            &laquo;
          </button>
          <button
            :disabled="currentPage === 1"
            :aria-label="t.prevPage"
            class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
            @click="currentPage--"
          >
            &lsaquo;
          </button>

          <span class="px-3 py-1">
            {{ t.page }} {{ currentPage }} {{ t.of }} {{ totalPages }}
          </span>

          <button
            :disabled="currentPage === totalPages"
            :aria-label="t.nextPage"
            class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
            @click="currentPage++"
          >
            &rsaquo;
          </button>
          <button
            :disabled="currentPage === totalPages"
            :aria-label="t.lastPage"
            class="px-3 py-1 border rounded hover:bg-gray-100 disabled:opacity-50"
            @click="currentPage = totalPages"
          >
            &raquo;
          </button>
        </nav>
      </div>

      <div v-else class="no-results text-center py-8 text-gray-600">
        {{ noResultsMessage }}
      </div>
    </div>

    <div v-if="loading" class="text-center py-8">
      <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-surface-dark"></div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'

export default {
  name: 'Search',
  props: {
    placeholder: {
      type: String,
      default: 'Suche...'
    },
    noResultsMessage: {
      type: String,
      default: 'Keine Ergebnisse gefunden.'
    },
    searchTerm: {
      type: String,
      default: ''
    },
    resultsPerPage: {
      type: Number,
      default: 10
    },
    // Endpoint der Such-API — pro Site/Route via data-endpoint überschreibbar
    endpoint: {
      type: String,
      default: '/api/search'
    },
    // UI-Texte überschreibbar (i18n); Keys siehe defaults in setup()
    labels: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const searchQuery = ref(props.searchTerm)
    const results = ref([])
    const loading = ref(false)
    const hasSearched = ref(false)
    const currentPage = ref(1)

    const totalPages = computed(() =>
      Math.ceil(results.value.length / props.resultsPerPage)
    )

    const paginatedResults = computed(() => {
      const start = (currentPage.value - 1) * props.resultsPerPage
      const end = start + props.resultsPerPage
      return results.value.slice(start, end)
    })

    const truncate = (text, length) => {
      if (!text || text.length <= length) return text
      return text.substring(0, length) + '...'
    }

    const t = computed(() => ({
      button: 'Suchen',
      loading: 'Suche läuft...',
      page: 'Seite',
      of: 'von',
      pagination: 'Seitennummerierung',
      firstPage: 'Erste Seite',
      prevPage: 'Vorherige Seite',
      nextPage: 'Nächste Seite',
      lastPage: 'Letzte Seite',
      ...props.labels
    }))

    // Läuft noch ein Request, wird er abgebrochen — sonst können sich
    // überlappende Antworten in falscher Reihenfolge auflösen (stale results).
    let abortController = null

    const performSearch = async () => {
      if (searchQuery.value.length < 2) return

      if (abortController) abortController.abort()
      abortController = new AbortController()
      const { signal } = abortController

      loading.value = true
      hasSearched.value = true
      currentPage.value = 1

      try {
        const response = await fetch(`${props.endpoint}?q=${encodeURIComponent(searchQuery.value)}`, { signal })
        if (response.ok) {
          const data = await response.json()
          results.value = data.results || []
        } else {
          // If API fails, show empty results
          console.warn('Search API not available, showing empty results')
          results.value = []
        }
      } catch (error) {
        if (error.name === 'AbortError') return // neuer Request hat übernommen
        console.error('Search error:', error)
        // If there's an error, we'll show a message but not break the page
        results.value = []
      } finally {
        if (!signal.aborted) {
          loading.value = false
        }
      }
    }

    onMounted(() => {
      if (searchQuery.value) {
        performSearch()
      }
    })

    return {
      searchQuery,
      results,
      loading,
      hasSearched,
      currentPage,
      totalPages,
      paginatedResults,
      truncate,
      performSearch,
      t
    }
  }
}
</script>
